<template>
  <div class="terminal-management">
    <div v-if="isAdmin" class="card shadow-sm mb-4">
      <div class="card-body">
        <h5 class="card-title">{{ $t('addNewTerminal') }}</h5>
        <p class="text-muted mb-3">{{ $t('addNewTerminalHelp') }}</p>
        <div class="input-group mb-3">
          <input type="text" class="form-control" :placeholder="$t('enterTerminalName')" v-model="newTerminalName" aria-label="New Terminal Name">
          <button type="button" class="btn btn-primary" @click="addTerminal">{{ $t('addTerminal') }}</button>
        </div>
      </div>
    </div>

    <div v-if="unregisteredTerminals.length" class="card shadow-sm mb-4">
      <div class="card-body">
        <h5 class="card-title">{{ $t('unregisteredTerminals') }}</h5>
        <p class="text-muted mb-3">{{ $t('unregisteredTerminalsHelp') }}</p>
        <ul class="list-group list-group-flush">
          <li v-for="terminal in unregisteredTerminals" :key="terminal.id" class="list-group-item d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ terminal.name }}</strong>
              <span class="badge bg-secondary ms-2" v-if="terminal.pin">{{ $t('pinCode') }}: {{ terminal.pin }}</span>
              <span class="badge bg-secondary ms-2" v-else>{{ $t('awaitingRegistration') }}</span>
            </div>
            <button class="btn btn-ouline-danger btn-sm" @click="confirmDelete(terminal, false)"><SvgIcon name="delete" /></button>
          </li>
        </ul>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <h5 class="card-title">{{ $t('registeredTerminals') }}</h5>
        <ul class="list-group list-group-flush">
          <li v-for="terminal in registeredTerminals" :key="terminal.id" class="list-group-item d-flex justify-content-between align-items-center">
            <div>
              <span :class="['terminal-status', terminal.connected ? 'connected' : 'disconnected']"></span>
              <strong>{{ terminal.name }}</strong>
            </div>
            <div class="d-flex align-items-center">
              <span class="version-pill">{{ $t('version') }}: {{ terminal.version }}</span>
              <span class="last-seen-pill">{{ formatLastSeen(terminal.last_called_at) }}</span>
              <button class="btn btn-outline-primary btn-sm ms-2" @click="editTerminal(terminal)">
                <SvgIcon name="edit" />
              </button>
              <button class="btn btn-outline-danger btn-sm ms-2" @click="confirmDelete(terminal, true)" v-if="isAdmin">
                <SvgIcon name="delete" />
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="modal fade" id="terminalConfigModal" tabindex="-1" ref="configModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('configureTerminal', { name: selectedTerminal?.name }) }}</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            
            <!-- Printing Settings Section -->
            <div class="config-section mb-4">
              <h6 class="section-title">{{ $t('printingSettings') }}</h6>
              <p class="section-description">{{ $t('printingSettingsHelp') }}</p>
              
              <div class="option-group mb-3">
                <div class="form-check form-switch" :class="{ 'mb-3': printing.active }">
                  <input class="form-check-input" type="checkbox" v-model="printing.active" id="print-active">
                  <label class="form-check-label fw-bold" for="print-active">{{ $t('printingActive') }}</label>
                </div>
                
                <div v-if="printing.active">
                  <div class="printing-options mt-3">
                    <div class="form-check mb-1">
                      <input class="form-check-input" type="checkbox" v-model="printing.printCustomerName" id="pr-name">
                      <label class="form-check-label" for="pr-name">{{ $t('printCustomerName') }}</label>
                    </div>
                    <div class="form-check mb-1">
                      <input class="form-check-input" type="checkbox" v-model="printing.printCustomTicketName" id="pr-ticket">
                      <label class="form-check-label" for="pr-ticket">{{ $t('printCustomTicketName') }}</label>
                    </div>
                    <div class="form-check mb-1">
                      <input class="form-check-input" type="checkbox" v-model="printing.printItems" id="pr-items">
                      <label class="form-check-label" for="pr-items">{{ $t('printItems') }}</label>
                    </div>
                    <div class="form-check mb-1">
                      <input class="form-check-input" type="checkbox" v-model="printing.printModifiers" id="pr-mods">
                      <label class="form-check-label" for="pr-mods">{{ $t('printModifiers') }}</label>
                    </div>
                    <div class="form-check mb-1">
                      <input class="form-check-input" type="checkbox" v-model="printing.printZeroModifiers" id="pr-zero">
                      <label class="form-check-label" for="pr-zero">{{ $t('printZeroModifiers') }}</label>
                    </div>
                    <div class="form-check mb-1">
                      <input class="form-check-input" type="checkbox" v-model="printing.printModifierPrices" id="pr-mod-prices">
                      <label class="form-check-label" for="pr-mod-prices">{{ $t('printModifierPrices') }}</label>
                    </div>
                    <div class="form-check mb-1">
                      <input class="form-check-input" type="checkbox" v-model="printing.printSpecialRequests" id="pr-special">
                      <label class="form-check-label" for="pr-special">{{ $t('printSpecialRequests') }}</label>
                    </div>
                  </div>

                  <hr />

                  <div class="form-check form-switch mb-2">
                    <input class="form-check-input" type="checkbox" v-model="printing.autoPrintReceipt" id="auto-print-receipt">
                    <label class="form-check-label small fw-bold" for="auto-print-receipt">{{ $t('autoPrintReceipt') }}</label>
                  </div>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" v-model="printing.allowManualReceiptPrint" id="manual-receipt-print">
                    <label class="form-check-label small fw-bold" for="manual-receipt-print">{{ $t('allowManualReceiptPrint') }}</label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Phone Login Section -->
            <div class="config-section mb-4">
              <h6 class="section-title">{{ $t('phoneLogin') }}</h6>
              <p class="section-description">{{ $t('phoneLoginHelpTerminal') }}</p>
              <div class="option-group">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="phoneLoginEnabled" id="phone-login-enabled">
                  <label class="form-check-label fw-bold" for="phone-login-enabled">{{ $t('enablePhoneLoginTerminal') }}</label>
                </div>
              </div>
            </div>

            <!-- Global Schedules Section -->
            <div class="config-section mb-4">
              <h6 class="section-title">{{ $t('globalSchedules') }}</h6>
              <p class="section-description">{{ $t('globalSchedulesHelp') }}</p>
              <div class="option-group">
                <div class="form-check main-option">
                  <input class="form-check-input" type="checkbox" v-model="allSchedules" @change="toggleAllSchedules" id="all-schedules">
                  <label class="form-check-label fw-bold" for="all-schedules">{{ $t('allSchedules') }}</label>
                </div>
                <div v-if="!allSchedules" class="sub-options">
                  <div class="form-check" v-for="schedule in availableSchedules" :key="schedule.ref">
                    <input class="form-check-input" type="checkbox" 
                      :value="schedule.ref" 
                      v-model="selectedSchedules"
                      :id="'schedule-' + schedule.ref">
                    <label class="form-check-label" :for="'schedule-' + schedule.ref">
                      {{ schedule.name }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Order Flows Section -->
            <div class="config-section mb-4">
              <h6 class="section-title">{{ $t('orderFlows') }}</h6>
              <p class="section-description">{{ $t('orderFlowsHelp') }}</p>
              <div class="option-group">
                <div class="form-check main-option">
                  <input class="form-check-input" type="checkbox" v-model="allOrderFlows" @change="toggleAllOrderFlows" id="all-flows">
                  <label class="form-check-label fw-bold" for="all-flows">{{ $t('allOrderFlows') }}</label>
                </div>
                <div v-if="!allOrderFlows" class="sub-options">
                  <div class="form-check" v-for="flow in availableOrderFlows" :key="flow.id">
                    <input class="form-check-input" type="checkbox" 
                      :value="flow.id" 
                      v-model="selectedOrderFlows"
                      :id="'flow-' + flow.id">
                    <label class="form-check-label" :for="'flow-' + flow.id">
                      {{ flow.name }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Per-Order Flow Overrides Section -->
            <div class="config-section" v-if="selectedOrderFlows.length">
              <h6 class="section-title">{{ $t('scheduleOverridesByOrderFlow') }}</h6>
              <p class="section-description">{{ $t('scheduleOverridesByOrderFlowHelp') }}</p>
              
              <div v-for="flow in selectedOrderFlows" :key="flow" class="flow-override mb-3">
                <div class="flow-header">
                  <strong class="flow-name">{{ availableOrderFlows.find(f => f.id === flow)?.name || flow }}</strong>
                </div>
                <div class="option-group">
                  <div class="form-check main-option">
                    <input class="form-check-input" type="checkbox" 
                           :id="'all-sched-' + flow" 
                           v-model="allOrderFlowSchedules[flow]" 
                           @change="toggleAllOrderFlowSchedules(flow)">
                    <label class="form-check-label" :for="'all-sched-' + flow">{{ $t('useGlobalSchedules') }}</label>
                  </div>
                  <div v-if="!allOrderFlowSchedules[flow]" class="sub-options">
                    <div class="form-check" v-for="schedule in availableSchedules" :key="'sched-' + flow + '-' + schedule.ref">
                      <input class="form-check-input" type="checkbox"
                        :value="schedule.ref"
                        :checked="isScheduleSelectedForFlow(flow, schedule.ref)"
                        @change="toggleScheduleForFlow(flow, schedule.ref)"
                        :id="'sched-' + flow + '-' + schedule.ref">
                      <label class="form-check-label" :for="'sched-' + flow + '-' + schedule.ref">
                        {{ schedule.name }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="saveTerminalConfig">{{ $t('saveChanges') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Modal } from 'bootstrap';

export default {
  data() {
    return {
      newTerminalName: '',
      selectedTerminal: null,
      selectedOrderFlows: [],
      selectedSchedules: [],
      allOrderFlows: true,
      allSchedules: true,
      phoneLoginEnabled: true,
      configModal: null,
      selectedOrderFlowSchedules: {},
      allOrderFlowSchedules: {},
      printing: {
        active: true,
        autoPrintReceipt: false,
        allowManualReceiptPrint: true,
        printCustomerName: false,
        printCustomTicketName: true,
        printItems: true,
        printModifiers: true,
        printZeroModifiers: true,
        printModifierPrices: false,
        printSpecialRequests: false
      }
    };
  },
  mounted() {
    this.$store.dispatch('admin/fetchTerminals');
    this.configModal = new Modal(this.$refs.configModal);
  },
  computed: {
    ...mapGetters("admin", ['isAdmin']),
    unregisteredTerminals() {
      return this.terminals?.filter(t => !t.registered) || [];
    },
    registeredTerminals() {
      return this.terminals?.filter(t => t.registered) || [];
    },
    terminals() {
      return this.$store.state.admin.terminals;
    },
    availableOrderFlows() {
      return this.$store.state.admin.config?.orderTypes || [];
    },
    availableSchedules() {
      return this.$store.state.admin.schedules || [];
    },
  },
  methods: {
    addTerminal() {
      if (this.newTerminalName.trim()) {
        const terminalData = {
          name: this.newTerminalName,
          locationId: this.$store.state.admin.activeLocation
        };
        this.$store.dispatch('admin/addTerminal', terminalData);
        this.newTerminalName = ''; // Clear the input after adding
      }
    },
    confirmDelete(terminal, isRegistered) {
      const message = isRegistered
        ? this.$t('confirmDeleteRegistered')
        : this.$t('confirmDeleteUnregistered');
      if (confirm(message)) {
        this.$store.dispatch('admin/deleteTerminal', terminal.id);
      }
    },
    saveTerminals() {
      this.$store.dispatch('admin/fetchTerminals');
    },
    formatLastSeen(lastSeenDate) {
      const now = new Date();
      const lastSeen = new Date(lastSeenDate);
      const diffTime = Math.abs(now - lastSeen);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 1) {
        return this.$t('today');
      }
      return this.$t('daysAgo', { count: diffDays });
    },
    editTerminal(terminal) {
      this.selectedTerminal = terminal;
      this.selectedOrderFlows = terminal.config?.orderFlows || [];
      this.selectedSchedules = terminal.config?.schedules || [];
      this.allOrderFlows = this.selectedOrderFlows.length === 0;
      this.allSchedules = this.selectedSchedules.length === 0;
      this.phoneLoginEnabled = terminal.config?.phoneLoginEnabled !== false;
      
      // Load printing settings
      const defaultPrinting = {
        active: true,
        autoPrintReceipt: false,
        allowManualReceiptPrint: true,
        printCustomerName: false,
        printCustomTicketName: true,
        printItems: true,
        printModifiers: true,
        printZeroModifiers: true,
        printModifierPrices: false,
        printSpecialRequests: false
      };
      
      this.printing = {
        ...defaultPrinting,
        ...(terminal.config?.printing || {})
      };

      // Per-order-flow schedules - re-initialize reactively
      const initialFlowSchedules = terminal.config?.orderFlowSchedules || {};
      this.selectedOrderFlowSchedules = {}; // Reset
      this.allOrderFlowSchedules = {}; // Reset
      (this.selectedOrderFlows || []).forEach(flow => {
        // Deep copy schedule array to prevent reference sharing
        const schedules = initialFlowSchedules[flow] ? [...initialFlowSchedules[flow]] : [];
        this.selectedOrderFlowSchedules[flow] = schedules;
        // Set the 'All' checkbox state reactively
        this.allOrderFlowSchedules[flow] = schedules.length === 0;
      });
      this.configModal.show();
    },
    closeModal() {
      this.configModal.hide();
      this.selectedTerminal = null;
      this.selectedOrderFlows = [];
      this.selectedSchedules = [];
      this.selectedOrderFlowSchedules = {};
      this.allOrderFlowSchedules = {};
      this.phoneLoginEnabled = true;
    },
    async saveTerminalConfig() {
      if (!this.selectedTerminal) return;
      
      const orderFlowSchedules = {};
      (this.selectedOrderFlows || []).forEach(flow => {
        if (!this.allOrderFlowSchedules[flow] && this.selectedOrderFlowSchedules[flow] && this.selectedOrderFlowSchedules[flow].length > 0) {
          orderFlowSchedules[flow] = this.selectedOrderFlowSchedules[flow];
        }
      });
      const config = {
        orderFlows: this.allOrderFlows ? null : this.selectedOrderFlows,
        schedules: this.allSchedules ? null : this.selectedSchedules,
        orderFlowSchedules: Object.keys(orderFlowSchedules).length > 0 ? orderFlowSchedules : undefined,
        // Only persist when disabled; absence means "enabled by default"
        phoneLoginEnabled: this.phoneLoginEnabled ? null : false,
        printing: this.printing
      };
      
      try {
        await this.$store.dispatch('admin/updateTerminalConfig', {
          terminalId: this.selectedTerminal.id,
          config
        });
        this.closeModal();
        this.$store.dispatch('admin/fetchTerminals');
      } catch (error) {
        console.error('Failed to update terminal config:', error);
      }
    },
    toggleAllOrderFlows() {
      if (this.allOrderFlows) {
        this.selectedOrderFlows = [];
      }
    },
    toggleAllSchedules() {
      if (this.allSchedules) {
        this.selectedSchedules = [];
      }
    },
    toggleAllOrderFlowSchedules(flow) {
      if (this.allOrderFlowSchedules[flow]) {
        this.selectedOrderFlowSchedules[flow] = [];
      }
    },
    isScheduleSelectedForFlow(flowId, scheduleRef) {
      return this.selectedOrderFlowSchedules[flowId]?.includes(scheduleRef);
    },
    toggleScheduleForFlow(flowId, scheduleRef) {
      const schedules = this.selectedOrderFlowSchedules[flowId];
      if (!schedules) return;

      const index = schedules.indexOf(scheduleRef);
      if (index > -1) {
        schedules.splice(index, 1);
      } else {
        schedules.push(scheduleRef);
      }
    }
  }
};
</script>

<style scoped>

.card {
  box-shadow: none !important;
}

.input-group .form-control {
  border-radius: 0.25rem;
}

.list-group-item {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.terminal-status {
  height: 10px;
  width: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
}

.connected {
  background-color: green;
}

.disconnected {
  background-color: grey;
}

.card {
  border-radius: 0.5rem;
}

.card-title {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.text-muted {
  font-size: 0.9rem;
}

.badge {
  font-size: 1rem;
  padding: 0.5em;
}

.version-pill {
  background-color: #6c757d;
  color: white;
  border-radius: 50px;
  padding: 0.25em 0.75em;
  font-size: 0.9rem;
  margin-right: 10px;
  display: inline-block;
}

.last-seen-pill {
  background-color: #adb5bd;
  color: white;
  border-radius: 50px;
  padding: 0.25em 0.75em;
  font-size: 0.9rem;
  margin-right: 10px;
  display: inline-block;
}

.modal-dialog {
  max-width: 600px;
}

.config-section {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.25rem;
  background-color: #f8f9fa;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.section-description {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.option-group {
  background-color: white;
  border-radius: 6px;
  padding: 1rem;
}

.main-option {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0.75rem;
}

.main-option:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.sub-options {
  padding-left: 1.5rem;
}

.sub-options .form-check {
  margin-bottom: 0.5rem;
}

.flow-override {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  background-color: white;
}

.flow-header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.flow-name {
  color: #495057;
  font-size: 1rem;
}

.form-check {
  margin-bottom: 0.5rem;
}

.form-check:last-child {
  margin-bottom: 0;
}

.form-label {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

</style>
